import { useCallback, useEffect, useState } from 'react';
import { EmployeeInput, EmployeePatch, type AreaDto, type EmployeeDto, type LoginAttemptDto, type Role } from '@app/shared';
import { ROLES, ROLE_LABEL, can as roleCan } from '@app/shared/core';
import { api } from '../../api';
import { platform } from '../../platform';
import { useApp } from '../../state';
import { Icon, Sheet, when } from '../../ui';
import { areaPath } from '../Panels';
import { SubHead } from './SubHead';

type Draft = { employee: EmployeeDto | null; preset?: { fullName: string; telegram: string } };

/** Сотрудники: вход в приложение только для тех, кто в этом списке. */
export function Staff() {
  const { fail, reloadMe, me } = useApp();
  const [list, setList] = useState<EmployeeDto[] | null>(null);
  const [attempts, setAttempts] = useState<LoginAttemptDto[]>([]);
  const [areas, setAreas] = useState<AreaDto[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);

  const load = useCallback(async () => {
    try {
      const [s, a, t] = await Promise.all([api.staff(), api.areas(), api.loginAttempts()]);
      setList(s);
      setAreas(a);
      setAttempts(t);
    } catch (e) {
      fail(e);
    }
  }, [fail]);
  useEffect(() => void load(), [load]);

  const active = list?.filter((e) => e.status === 'active') ?? [];
  const blocked = list?.filter((e) => e.status === 'blocked') ?? [];

  const row = (e: EmployeeDto) => (
    <button key={e.id} type="button" className="item" onClick={() => setDraft({ employee: e })}>
      <span className="grow stack" style={{ gap: 2 }}>
        <span className="title ellipsis">
          {e.fullName}
          {e.id === me.employee?.id ? ' (вы)' : ''}
        </span>
        <span className="sub ellipsis">
          {ROLE_LABEL[e.role]}
          {roleCan(e.role, 'areas.all') ? ' · все участки' : e.areaIds.length ? ` · ${e.areaIds.map((id) => areaPath(areas, id)).join(', ')}` : ' · участки не назначены'}
        </span>
        <span className="sub ellipsis">
          {e.lastSeenAt ? `входил ${when(e.lastSeenAt)}` : 'ещё не входил'}
          {e.tgUsername ? ` · @${e.tgUsername}` : e.userId ? ` · ID ${e.userId}` : ''}
          {e.phone ? ` · +${e.phone}` : ''}
        </span>
      </span>
      {!e.lastSeenAt && e.status === 'active' && <span className="badge outline">ждём входа</span>}
      <span className="chev">
        <Icon name="right" size={18} />
      </span>
    </button>
  );

  return (
    <div className="stack">
      <SubHead title="Сотрудники">
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setDraft({ employee: null })}>
          <Icon name="plus" size={16} />
          Добавить
        </button>
      </SubHead>
      <div className="card flat small">Войти могут только сотрудники из этого списка. Добавьте человека по Telegram ID, @username или телефону — при первом входе запись привяжется к его Telegram.</div>

      {attempts.length > 0 && (
        <>
          <h2 className="section-title">Пытались войти · {attempts.length}</h2>
          <div className="list">
            {attempts.map((a) => {
              const name = [a.lastName, a.firstName].filter(Boolean).join(' ');
              return (
                <div key={a.userId} className="item">
                  <span className="grow stack" style={{ gap: 2 }}>
                    <span className="title ellipsis">{name}</span>
                    <span className="sub ellipsis">
                      {a.username ? `@${a.username} · ` : ''}ID {a.userId} · {when(a.lastSeenAt)}
                    </span>
                  </span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDraft({ employee: null, preset: { fullName: name, telegram: String(a.userId) } })}>
                    Добавить
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {!list && <div className="skeleton" />}
      {active.length > 0 && (
        <>
          <h2 className="section-title">В списке · {active.length}</h2>
          <div className="list">{active.map(row)}</div>
        </>
      )}
      {blocked.length > 0 && (
        <>
          <h2 className="section-title">Доступ закрыт · {blocked.length}</h2>
          <div className="list">{blocked.map(row)}</div>
        </>
      )}
      {draft && (
        <EmployeeSheet
          draft={draft}
          areas={areas}
          isSelf={draft.employee?.id === me.employee?.id}
          onClose={() => setDraft(null)}
          onSaved={async () => {
            setDraft(null);
            await Promise.all([load(), reloadMe()]);
          }}
        />
      )}
    </div>
  );
}

function EmployeeSheet({ draft, areas, isSelf, onClose, onSaved }: { draft: Draft; areas: AreaDto[]; isSelf: boolean; onClose: () => void; onSaved: () => Promise<void> }) {
  const { fail, toast } = useApp();
  const e = draft.employee;
  const linked = !!e?.lastSeenAt;
  const [fullName, setFullName] = useState(e?.fullName ?? draft.preset?.fullName ?? '');
  const [position, setPosition] = useState(e?.position ?? '');
  const [telegram, setTelegram] = useState(draft.preset?.telegram ?? (e ? (e.userId ? String(e.userId) : e.tgUsername ? `@${e.tgUsername}` : '') : ''));
  const [phone, setPhone] = useState(e?.phone ? `+${e.phone}` : '');
  const [role, setRole] = useState<Role>(e?.role ?? 'operator');
  const [areaIds, setAreaIds] = useState<string[]>(e?.areaIds ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const allAreas = roleCan(role, 'areas.all');

  async function run(fn: () => Promise<unknown>, done: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      platform.haptic('success');
      toast(done);
      await onSaved();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  function save() {
    const contact = linked ? {} : { telegram: telegram.trim() || null, phone: phone.trim() || null };
    if (!e) {
      const p = EmployeeInput.safeParse({ fullName, position: position || null, role, areaIds, ...contact });
      if (!p.success) return setError(p.error.issues[0]?.message ?? 'Проверьте поля');
      return void run(() => api.createEmployee({ fullName, position: position || null, role, areaIds, ...contact }), 'Сотрудник добавлен');
    }
    const patch = { fullName, position: position || null, areaIds, ...(isSelf ? {} : { role }), ...contact };
    const p = EmployeePatch.safeParse(patch);
    if (!p.success) return setError(p.error.issues[0]?.message ?? 'Проверьте поля');
    void run(() => api.patchEmployee(e.id, patch), 'Сохранено');
  }

  const toggleArea = (id: string) => setAreaIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const sortedAreas = [...areas].sort((a, b) => areaPath(areas, a.id).localeCompare(areaPath(areas, b.id), 'ru'));

  return (
    <Sheet title={e ? e.fullName : 'Новый сотрудник'} onClose={onClose}>
      <div className="field">
        <label htmlFor="sf">Фамилия и имя</label>
        <input id="sf" className="input" value={fullName} onChange={(ev) => setFullName(ev.target.value)} placeholder="Иванов Иван" />
      </div>
      <div className="field">
        <label htmlFor="sp">Должность</label>
        <input id="sp" className="input" value={position} onChange={(ev) => setPosition(ev.target.value)} placeholder="Наладчик, линия 2" />
      </div>
      {linked ? (
        <div className="small muted">
          Telegram привязан: {e!.tgUsername ? `@${e!.tgUsername} · ` : ''}ID {e!.userId}
          {e!.phone ? ` · +${e!.phone}` : ''}
        </div>
      ) : (
        <>
          <div className="field">
            <label htmlFor="st">Telegram: ID или @username</label>
            <input id="st" className="input" value={telegram} autoCapitalize="off" autoCorrect="off" onChange={(ev) => setTelegram(ev.target.value)} placeholder="1014614490 или @ivanov" />
            <span className="hint">ID надёжнее: @username человек может сменить. ID видно на экране входа у самого сотрудника.</span>
          </div>
          <div className="field">
            <label htmlFor="sph">Или телефон</label>
            <input id="sph" className="input" value={phone} inputMode="tel" onChange={(ev) => setPhone(ev.target.value)} placeholder="+7 916 123-45-67" />
            <span className="hint">Сотрудник войдёт кнопкой «Войти по номеру телефона» — Telegram подтвердит, что номер его.</span>
          </div>
        </>
      )}

      <div className="field">
        <span className="label">Роль</span>
        <div className="chips" role="group" aria-label="Роль">
          {ROLES.map((r) => (
            <button key={r} type="button" className="chip" aria-pressed={role === r} disabled={isSelf} onClick={() => setRole(r)}>
              {ROLE_LABEL[r]}
            </button>
          ))}
        </div>
        {isSelf && <span className="hint">Свою роль меняет другой администратор.</span>}
      </div>

      <div className="field">
        <span className="label">Какое оборудование видит</span>
        {allAreas ? (
          <span className="hint">Роль «{ROLE_LABEL[role]}» видит все участки.</span>
        ) : areas.length === 0 ? (
          <span className="hint">Участков ещё нет — создайте их в разделе «Оборудование».</span>
        ) : (
          <div className="stack" style={{ gap: 2 }}>
            {sortedAreas.map((a) => (
              <label key={a.id} className="check">
                <input type="checkbox" checked={areaIds.includes(a.id)} onChange={() => toggleArea(a.id)} />
                <span>{areaPath(areas, a.id)}</span>
              </label>
            ))}
            <span className="hint">Вложенные участки открываются вместе с родительским. Открыл панель — может ей управлять.</span>
          </div>
        )}
      </div>

      {error && <p className="error">{error}</p>}
      <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={save}>
        {e ? 'Сохранить' : 'Добавить в список'}
      </button>
      {e && !isSelf && (
        <button
          type="button"
          className="btn btn-danger btn-block"
          disabled={busy}
          onClick={() =>
            e.status === 'blocked'
              ? void run(() => api.patchEmployee(e.id, { status: 'active' }), 'Доступ возвращён')
              : void platform
                  .confirm(`Закрыть доступ: ${e.fullName}? Его открытые подключения к панелям оборвутся сразу.`)
                  .then((ok) => {
                    if (ok) void run(() => api.patchEmployee(e.id, { status: 'blocked' }), 'Доступ закрыт');
                  })
          }
        >
          {e.status === 'blocked' ? 'Вернуть доступ' : 'Закрыть доступ'}
        </button>
      )}
      {e && !isSelf && !e.lastSeenAt && (
        <button type="button" className="btn btn-ghost btn-block" disabled={busy} onClick={() => void run(() => api.deleteEmployee(e.id), 'Удалён из списка')}>
          Удалить из списка
        </button>
      )}
    </Sheet>
  );
}
